import React, { useState } from 'react';
import {
  Box,
  Typography,
  Chip,
  Button,
  Divider,
  Grid,
} from '@mui/material';
import OpenInNewRoundedIcon    from '@mui/icons-material/OpenInNewRounded';
import CompareArrowsRoundedIcon from '@mui/icons-material/CompareArrowsRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import CodeComparisonModal from './CodeComparisonModal';
import {
  DANGER,
  WARNING,
  SUCCESS,
  INDIGO,
  SURFACE_LIGHT,
} from '../../utils/colors';

// ── Helpers ───────────────────────────────────────────────────────────────────

function similarityColor(pct) {
  if (pct > 70) return DANGER;
  if (pct > 40) return WARNING;
  return SUCCESS;
}

function riskColor(score) {
  if (score > 60) return WARNING;
  if (score > 80) return DANGER;
  return SUCCESS;
}

function formatSeconds(sec) {
  if (sec == null) return '—';
  if (sec < 60)   return `${Math.round(sec)}s`;
  const m = Math.floor(sec / 60);
  const s = Math.round(sec % 60);
  return s > 0 ? `${m}m ${s}s` : `${m}m`;
}

// ── Stat card ─────────────────────────────────────────────────────────────────

function StatCard({ label, value, accent, icon }) {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
        p: 1.5,
        borderRadius: 2,
        bgcolor: (theme) =>
          theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : SURFACE_LIGHT,
        border: `1.5px solid ${accent}30`,
        minWidth: 110,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: accent }}>
        {icon}
        <Typography variant="caption" fontWeight={700} color={accent} noWrap>
          {label}
        </Typography>
      </Box>
      <Typography variant="h6" fontWeight={800} sx={{ color: accent, lineHeight: 1.1 }}>
        {value}
      </Typography>
    </Box>
  );
}

// ── Timeline entry ────────────────────────────────────────────────────────────

function TimelineEntry({ label, value, color }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Box
        sx={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          bgcolor: color || 'text.secondary',
          flexShrink: 0,
        }}
      />
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={600} sx={{ ml: 'auto' }}>
        {value}
      </Typography>
    </Box>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

/**
 * ExpandableRowPanel
 *
 * Renders the detail panel displayed when a plagiarism table row is expanded.
 *
 * Props:
 *   row  {object}  – a single item from the analytics API response
 */
export default function ExpandableRowPanel({ row }) {
  const [compareOpen, setCompareOpen] = useState(false);

  if (!row) return null;

  const simColor  = similarityColor(row.winnowingSimilarity ?? 0);
  const rColor    = riskColor(row.aiRiskScore ?? 0);

  return (
    <>
      <Box
        sx={{
          px: { xs: 2, md: 4 },
          py: 2.5,
          bgcolor: (theme) =>
            theme.palette.mode === 'dark'
              ? 'rgba(79,70,229,0.05)'
              : 'rgba(79,70,229,0.03)',
          borderTop: `2px solid ${INDIGO}30`,
        }}
      >
        <Grid container spacing={3} alignItems="flex-start">

          {/* ── Left: core metrics ──────────────────────────────────────── */}
          <Grid item xs={12} sm="auto">
            <Typography
              variant="overline"
              color="text.secondary"
              fontWeight={700}
              sx={{ letterSpacing: '0.08em' }}
            >
              Metrics
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mt: 1 }}>
              <StatCard
                label="Similarity"
                value={`${row.winnowingSimilarity ?? 0}%`}
                accent={simColor}
                icon={<WarningAmberRoundedIcon sx={{ fontSize: 14 }} />}
              />
              <StatCard
                label="AI Risk Score"
                value={row.aiRiskScore != null ? `${row.aiRiskScore}/100` : '—'}
                accent={rColor}
                icon={<WarningAmberRoundedIcon sx={{ fontSize: 14 }} />}
              />
            </Box>
          </Grid>

          {/* ── Centre: submission timeline ─────────────────────────────── */}
          <Grid item xs={12} sm>
            <Typography
              variant="overline"
              color="text.secondary"
              fontWeight={700}
              sx={{ letterSpacing: '0.08em' }}
            >
              Submission Timeline
            </Typography>
            <Box
              sx={{
                mt: 1,
                p: 1.5,
                borderRadius: 2,
                bgcolor: (theme) =>
                  theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : SURFACE_LIGHT,
                display: 'flex',
                flexDirection: 'column',
                gap: 0.75,
                minWidth: 220,
              }}
            >
              <TimelineEntry
                label="Problem"
                value={row.problemSlug}
                color={INDIGO}
              />
              <Divider sx={{ my: 0.25 }} />
              <TimelineEntry
                label="Submission time"
                value={formatSeconds(row.submissionTimeSeconds)}
                color={
                  row.submissionTimeSeconds != null && row.submissionTimeSeconds < 60
                    ? DANGER
                    : row.submissionTimeSeconds != null && row.submissionTimeSeconds < 120
                    ? WARNING
                    : SUCCESS
                }
              />
              <TimelineEntry
                label="Checked at"
                value={row.checkedAt ? new Date(row.checkedAt).toLocaleString() : '—'}
                color="text.secondary"
              />
              <TimelineEntry
                label="Flagged"
                value={row.flagged ? 'Yes' : 'No'}
                color={row.flagged ? DANGER : SUCCESS}
              />
            </Box>
          </Grid>

          {/* ── Right: matched submission info + actions ─────────────────── */}
          <Grid item xs={12} sm="auto">
            <Typography
              variant="overline"
              color="text.secondary"
              fontWeight={700}
              sx={{ letterSpacing: '0.08em' }}
            >
              Matched Submission
            </Typography>
            <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Chip
                  label={row.studentA}
                  size="small"
                  sx={{
                    bgcolor: `${INDIGO}18`,
                    color: INDIGO,
                    fontWeight: 700,
                    fontSize: '0.72rem',
                  }}
                />
                <Typography variant="caption" color="text.secondary">vs</Typography>
                <Chip
                  label={row.studentB}
                  size="small"
                  sx={{
                    bgcolor: `${DANGER}18`,
                    color: DANGER,
                    fontWeight: 700,
                    fontSize: '0.72rem',
                  }}
                />
              </Box>

              <Box
                component="a"
                href={`https://leetcode.com/problems/${row.problemSlug}/`}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.5,
                  fontSize: '0.78rem',
                  color: INDIGO,
                  textDecoration: 'none',
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                <OpenInNewRoundedIcon sx={{ fontSize: 14 }} />
                View on LeetCode
              </Box>

              <Button
                variant="contained"
                size="small"
                startIcon={<CompareArrowsRoundedIcon />}
                onClick={() => setCompareOpen(true)}
                sx={{
                  mt: 0.5,
                  bgcolor: INDIGO,
                  '&:hover': { bgcolor: '#4338ca' },
                  fontWeight: 700,
                  textTransform: 'none',
                  borderRadius: 1.5,
                }}
              >
                View Side-by-Side Code
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* ── Code comparison modal ───────────────────────────────────────── */}
      <CodeComparisonModal
        open={compareOpen}
        onClose={() => setCompareOpen(false)}
        row={row}
      />
    </>
  );
}
